<template>
    <div
        class="sbs-accordion-item"
        :data-active="isOpened">
        <component
            :is="headerButtonComponent"
            class="sbs-accordion-item__header-button trs"
            :class="accordionHeaderClass"
            @click="headerButtonClickHandler">
            <span class="sbs-accordion-item__header-wrapper">
                <slot
                    name="iconHeader"
                    v-bind="{ opened: isOpened }">
                    <span
                        v-if="iconName"
                        class="sbs-accordion-item__header-icon">
                        <SbsIcon
                            :colorTheme="iconColorTheme"
                            :iconName="iconName"
                            :iconGroup="iconGroup"
                            :iconSize="iconSize" />
                    </span>
                </slot>
                <slot
                    name="header"
                    v-bind="{ opened: isOpened }"></slot>
            </span>
            <component
                :is="arrowComponent"
                class="sbs-accordion-item__arrow trs-slow"
                v-bind="arrowData"
                @click="arrowClickHandler">
                <slot
                    name="arrowIcon"
                    v-bind="{ opened: isOpened }">
                    <SbsIcon
                        iconName="chevron-down"
                        width="16"
                        height="16" />
                </slot>
            </component>
        </component>

        <transition
            name="expand"
            @enter="enter"
            @afterEnter="afterEnter"
            @leave="leave">
            <div
                v-show="isOpened"
                class="sbs-accordion-item__content-wrapper trs-slow">
                <div class="sbs-accordion-item__content">
                    <slot
                        name="content"
                        v-bind="{ opened: isOpened }"></slot>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { isArrayContainsValue } from '../../utils/validators.js';
import { getRandomId } from '../../utils/helpers.js';
import {
    AllSizes,
    APP_SIZES,
} from '../../utils/sizes.js';
import { AllColors } from '../../utils/colors.js';
import {
    AllIconGroups,
    ICON_GROUPS,
} from '../icon/utils/consts.js';
import expandTransition from '../../mixins/expandTransition.js';
import SbsBaseButton from '../button/SbsBaseButton.vue';

/**
 * Компонент элемента аккордеона
 */
export default {
    name: 'SbsAccordionItem',
    components: {
        SbsBaseButton,
    },
    mixins: [expandTransition],
    inject: {
        accordionComponent: { default: null },
    },
    computed: {
        accordionValue() {
            return this.accordionComponent?.modelValue;
        },
        isOpened() {
            return Array.isArray(this.accordionValue)
                ? this.accordionValue.includes(this.itemId)
                : this.accordionValue === this.itemId;
        },
        itemId() {
            return this.id || `sbs-accordion-item-${getRandomId()}`;
        },
        headerButtonComponent() {
            return this.openOnArrowClickOnly ? 'div' : SbsBaseButton;
        },
        arrowComponent() {
            return this.openOnArrowClickOnly ? SbsBaseButton : 'span';
        },
        arrowClasses() {
            return [
                {
                    'fa-rotate-180': this.isOpened,
                },
            ];
        },
        arrowData() {
            const data = {
                class: this.arrowClasses,
            };

            if (this.openOnArrowClickOnly) {
                data.type = 'button';
            }

            return data;
        },
    },
    methods: {
        emitAccordionToggleEvent(event) {
            /**
             * Событие 'SbsAccordion:change' срабатывает при изменении значения,
             * если SbsAccordionItem используется в связке с комопнентом SbsAccordion.
             * В качестве параметра - значение после изменения
             */
            if (!this.accordionComponent) return;

            this.accordionComponent.accordionChangeHandler(this.itemId, event);
        },
        headerButtonClickHandler(event) {
            if (!this.openOnArrowClickOnly) {
                this.emitAccordionToggleEvent(event);
            }
        },
        arrowClickHandler(event) {
            if (this.openOnArrowClickOnly) {
                event.stopPropagation();
                this.emitAccordionToggleEvent(event);
            }
        },
    },
    props: {
        /**
         * Класс для заголовка элемента аккордеона
         */
        accordionHeaderClass: {
            type: [String, Object, Array],
            default: '',
        },
        /**
         * Стилизует цвет иконки под цветовые темы
         */
        iconColorTheme: {
            type: String,
            default: AllColors.CURRENT_COLOR,
        },
        /**
         * Тип иконки
         */
        iconGroup: {
            type: String,
            default: AllIconGroups.REGULAR,
            validator: isArrayContainsValue(ICON_GROUPS),
        },
        /**
         * Название иконки из FontAwesome.
         * Список всех иконок можно посмотреть [здесь](https://fontawesome.com/v6/icons)
         */
        iconName: {
            type: String,
            default: '',
        },
        /**
         * Размер иконки
         */
        iconSize: {
            type: String,
            default: AllSizes.MEDIUM,
            validator: isArrayContainsValue(APP_SIZES),
        },
        /**
         * Уникальный идентификатор
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Включает открытие аккордеона только по клику на стрелку
         */
        openOnArrowClickOnly: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        isOpened(value) {
            if (value) {
                /**
                 * Эмит события `open`, срабатывает при открытии аккордеона
                 */
                this.$emit('open', this.itemId);
            } else {
                /**
                 * Эмит события `close`, срабатывает при закрытии аккордеона
                 */
                this.$emit('close', this.itemId);
            }
        },
    },
};
</script>

<style lang="scss">
.sbs-accordion-item {
    display: flex;
    flex-direction: column;
    width: 100%;
    border-bottom: 1px solid $gray-light;

    &:last-child {
        border-bottom: none;
    }

    &__header-button {
        display: flex;
        align-items: center;
        text-align: left;

        @include typo-primary-demi;
    }

    &__header-wrapper {
        display: flex;
        flex-grow: 1;
        align-items: center;
        width: 100%;
        min-width: 0;
    }

    &__header-icon {
        flex-shrink: 0;
        margin-right: 15px;
    }

    &__arrow {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        padding: 0;
        font: inherit;
        background-color: $transparent;
        border: none;
        transition-property: transform, opacity;
    }

    &__content-wrapper {
        @include typo-additional-book;
    }
}
</style>
